import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageOpen, Plus, Loader2, X, CheckCircle, Check, Ban } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

type GiftStatus = 'pending' | 'attached' | 'delivered' | 'cancelled';

interface PendingGift {
  id: number;
  phone: string;
  giftCatalogId: number;
  description?: string;
  quantity: number;
  status: GiftStatus;
  sourceEvent?: string;
  referrerId?: number | null;
  attachedOrderId?: number | string | null;
  createdAt?: number;
  attachedAt?: number | null;
  giftKey?: string;
  giftName?: string;
}

interface GiftCatalogItem {
  id: number;
  key: string;
  name: string;
  description?: string;
  isActive: number | boolean;
}

function toBool(v: number | boolean | undefined): boolean {
  return v === 1 || v === true;
}

function formatDateTime(ts?: number | null): string {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleString('zh-HK');
}

const STATUS_OPTIONS = [
  { value: 'pending', label: '待送出' },
  { value: 'attached', label: '已隨單' },
  { value: 'delivered', label: '已交付' },
  { value: 'cancelled', label: '已取消' },
  { value: '', label: '全部' },
];

const STATUS_LABELS: Record<string, string> = {
  pending: '待送出',
  attached: '已隨單',
  delivered: '已交付',
  cancelled: '已取消',
};

function statusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    attached: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  return map[status] || map.cancelled;
}

const defaultForm = {
  phone: '',
  giftKey: '',
  description: '',
};

const AdminPendingGifts: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const [gifts, setGifts] = useState<PendingGift[]>([]);
  const [catalog, setCatalog] = useState<GiftCatalogItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadGifts();
  }, [token, navigate, statusFilter]);

  useEffect(() => {
    if (!token) return;
    loadCatalog();
  }, [token, navigate]);

  const handleUnauthorized = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const loadGifts = async () => {
    setLoading(true);
    setError(null);
    try {
      const query = statusFilter ? `?status=${statusFilter}` : '';
      const res = await apiFetch(`/api/public/admin/pending-gifts${query}`);
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '獲取待送禮品失敗');
      setGifts(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '獲取待送禮品失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    try {
      const res = await apiFetch('/api/public/admin/gift-catalog');
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => []);
      if (res.ok && Array.isArray(data)) {
        setCatalog(data.filter((g: GiftCatalogItem) => toBool(g.isActive)));
      }
    } catch {
      // 目錄只用於新增 Modal 下拉，失敗時不阻塞頁面
    }
  };

  const openCreate = () => {
    setForm(defaultForm);
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(defaultForm);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!form.phone.trim()) {
      setFormError('請填寫客戶電話');
      return;
    }
    if (!form.giftKey) {
      setFormError('請選擇禮品');
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch('/api/public/admin/pending-gifts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: form.phone.trim(),
          giftKey: form.giftKey,
          ...(form.description.trim() ? { description: form.description.trim() } : {}),
        }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setSuccess('待送禮品已建立');
      closeModal();
      loadGifts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setFormError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id: number, status: GiftStatus) => {
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/pending-gifts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '更新狀態失敗');
      setSuccess(status === 'delivered' ? '已標記為已交付' : '禮品已取消');
      loadGifts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || '更新狀態失敗');
    }
  };

  const handleCancel = (id: number) => {
    if (!confirm('確定要取消這份待送禮品嗎？')) return;
    updateStatus(id, 'cancelled');
  };

  return (
    <AdminLayout currentPage="pending-gifts" onLogout={handleUnauthorized}>
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <PackageOpen className="w-7 h-7 text-orange-600" />
            待送禮品
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            已掛到客戶名下、等待隨單送出的禮品。客戶落單時系統會自動把待送禮品附加到訂單。
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
          >
            <Plus size={20} /> 新增待送禮品
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <tr>
                <th className="p-4">ID</th>
                <th className="p-4">客戶電話</th>
                <th className="p-4">禮品</th>
                <th className="p-4">來源</th>
                <th className="p-4">建立時間</th>
                <th className="p-4">狀態</th>
                <th className="p-4 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> 加載中...
                  </td>
                </tr>
              ) : gifts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    暫無{statusFilter ? STATUS_LABELS[statusFilter] : ''}禮品記錄
                  </td>
                </tr>
              ) : (
                gifts.map((gift) => (
                  <tr key={gift.id} className="hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="p-4 text-gray-600 dark:text-gray-400">{gift.id}</td>
                    <td className="p-4 text-gray-800 dark:text-white font-medium whitespace-nowrap">{gift.phone}</td>
                    <td className="p-4">
                      <div className="text-gray-800 dark:text-white font-medium">
                        {gift.giftName || gift.giftKey || `#${gift.giftCatalogId}`}
                        {gift.quantity > 1 && <span className="text-gray-500 dark:text-gray-400"> × {gift.quantity}</span>}
                      </div>
                      {gift.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate" title={gift.description}>
                          {gift.description}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="text-gray-600 dark:text-gray-300">
                        {gift.sourceEvent === 'manual' ? '手動掛贈' : gift.sourceEvent || '-'}
                      </div>
                      {gift.attachedOrderId && (
                        <button
                          onClick={() => navigate(`/admin/orders/${gift.attachedOrderId}`)}
                          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                        >
                          #{gift.attachedOrderId}
                        </button>
                      )}
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDateTime(gift.createdAt)}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(gift.status)}`}>
                        {STATUS_LABELS[gift.status] || gift.status}
                      </span>
                    </td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      {(gift.status === 'pending' || gift.status === 'attached') && (
                        <button
                          onClick={() => updateStatus(gift.id, 'delivered')}
                          className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 text-xs"
                        >
                          <Check size={14} /> 標記已交付
                        </button>
                      )}
                      {gift.status === 'pending' && (
                        <button
                          onClick={() => handleCancel(gift.id)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs"
                        >
                          <Ban size={14} /> 取消
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">新增待送禮品</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  客戶電話 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="852XXXXXXXX"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  禮品 <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={form.giftKey}
                  onChange={(e) => setForm((f) => ({ ...f, giftKey: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                >
                  <option value="">請選擇禮品</option>
                  {catalog.map((g) => (
                    <option key={g.id} value={g.key}>{g.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">備註（可選）</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="覆寫禮品說明或加上備註"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div className="flex gap-2 pt-2 sticky bottom-0 bg-white dark:bg-gray-800 pb-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : '建立'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminPendingGifts;
